/**
 * Rendering helpers for the Discord profile embed (DiscordEmbed.vue).
 * Palettes, color math and effect parameters are ported from Discord's web
 * client so the card looks like the profile does on Discord.
 */
import { hexToRgb, rgbToHex } from '~/composables/useLayoutStyles'

export interface DiscordDisplayNameStyles {
  fontId: number
  effectId: number
  colors: string[]
}

type Rgb = [number, number, number]
// h 0-360, s and l 0-1 (chroma.js conventions, which Discord uses)
type Hsl = [number, number, number]

function hslToHex(hsl: Hsl): string {
  return rgbToHex(...hslToRgb(hsl))
}

function rgbToHsl([r, g, b]: Rgb): Hsl {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  if (max === min) return [0, 0, l]
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h: number
  if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0)
  else if (max === gn) h = (bn - rn) / d + 2
  else h = (rn - gn) / d + 4
  return [h * 60, s, l]
}

function hslToRgb([h, s, l]: Hsl): Rgb {
  if (s === 0) return [l * 255, l * 255, l * 255]
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const hue = (t: number) => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  const hn = h / 360
  return [hue(hn + 1 / 3) * 255, hue(hn) * 255, hue(hn - 1 / 3) * 255]
}

function withLightness(hex: string, l: number): string {
  const [h, s] = rgbToHsl(hexToRgb(hex))
  return hslToHex([h, s, l])
}

function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }) as Rgb
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

// ---------------------------------------------------------------------------
// Display name styles
// ---------------------------------------------------------------------------

const EFFECT = {
  SOLID: 1,
  GRADIENT: 2,
  NEON: 3,
  TOON: 4,
  POP: 5,
  GLOW: 6,
  PRISM: 7,
  GUMMY: 8
} as const

export type DisplayNameEffect = 'solid' | 'gradient' | 'prism' | 'neon' | 'toon' | 'pop' | 'gummy'

// Fonts with a matching .dns-font-{id} class in DiscordDisplayName.vue
// (11 is Discord's default gg sans, which keeps the page font)
const KNOWN_FONT_IDS = [3, 4, 6, 7, 8, 10, 12, 13, 14, 15, 16]

// Discord's dark theme background (--background-base-low) and the toon backdrop,
// used for the same readability check Discord runs on name colors
const DARK_BACKGROUND = '#202024'
const TOON_BACKGROUND = '#333333'

const MIN_CONTRAST: Record<number, number> = {
  [EFFECT.SOLID]: 3,
  [EFFECT.GRADIENT]: 2.5,
  [EFFECT.GLOW]: 2.5,
  [EFFECT.PRISM]: 2.5,
  [EFFECT.NEON]: 3,
  [EFFECT.TOON]: 3,
  [EFFECT.POP]: 3,
  [EFFECT.GUMMY]: 3
}

// Discord's minimum prism cycle width
const PRISM_MIN_CYCLE_PX = 38

/** Lighten a color in 5% steps until it reads on the dark background */
function ensureContrast(color: string, background: string, ratio: number): string {
  const [h, s, startL] = rgbToHsl(hexToRgb(color))
  let l = startL
  let current = color
  for (let i = 0; i < 10 && contrastRatio(current, background) < ratio && l < 0.95; i++) {
    l += 0.05
    current = hslToHex([h, s, l])
  }
  return current
}

/** Shades Discord derives from the main color for neon, toon and pop */
function colorVariants(main: string) {
  const [h, s, l] = rgbToHsl(hexToRgb(main))
  const neonS = Math.min(1, 1.2 * s)
  const neonL = Math.min(0.6, l + 0.1)
  // Flicker frame of the neon effect: hsl(h, s * 1.1 + 10, 85)
  const flickerS = Math.min(1, s * 100) * (s * 100 * 1.1 + 10)
  return {
    '--dns-main': main,
    '--dns-light-1': withLightness(main, Math.min(1, 1.2 * l)),
    '--dns-light-2': withLightness(main, Math.min(1, 1.6 * l)),
    '--dns-dark-2': withLightness(main, Math.max(0, 0.2 * l)),
    '--dns-toon-stroke-color': withLightness(main, Math.max(0.12, 0.4 * l)),
    '--dns-neon-stroke': hslToHex([h, neonS, neonL]),
    '--dns-neon-flicker': `hsl(${h.toFixed(1)} ${Math.min(100, flickerS).toFixed(1)}% 85%)`
  }
}

export interface DisplayNameStyle {
  effect: DisplayNameEffect
  fontClass: string | null
  /** Colors after the readability check, cycled per letter for gummy */
  colors: string[]
  vars: Record<string, string>
}

export function getDisplayNameStyle(styles: DiscordDisplayNameStyles, name: string): DisplayNameStyle | null {
  const raw = styles.colors.filter(c => /^#[0-9a-f]{6}$/i.test(c))
  if (raw.length === 0) return null

  const { effectId } = styles
  const background = effectId === EFFECT.TOON ? TOON_BACKGROUND : DARK_BACKGROUND
  const colors = raw.map(c => ensureContrast(c, background, MIN_CONTRAST[effectId] ?? 3))
  const main = colors[0]!

  let effect: DisplayNameEffect
  let vars: Record<string, string>

  switch (effectId) {
    case EFFECT.GRADIENT:
    case EFFECT.GLOW:
      effect = 'gradient'
      vars = {
        '--dns-main': main,
        '--dns-gradient-stops': colors.length === 1
          ? `${main}, ${main}`
          : colors.map((c, i) => `${c} ${10 + (80 * i) / (colors.length - 1)}%`).join(', ')
      }
      break
    case EFFECT.PRISM:
      effect = 'prism'
      vars = {
        '--dns-main': main,
        '--dns-prism-stops': Array.from({ length: colors.length + 1 }, (_, i) =>
          `${colors[i % colors.length]} ${((100 * i) / colors.length).toFixed(3)}%`
        ).join(', '),
        '--dns-prism-cycle': `max(${name.length}ch, ${PRISM_MIN_CYCLE_PX}px)`
      }
      break
    case EFFECT.NEON:
      effect = 'neon'
      vars = colorVariants(main)
      break
    case EFFECT.TOON:
      effect = 'toon'
      vars = colorVariants(main)
      break
    case EFFECT.POP:
      effect = 'pop'
      vars = colorVariants(main)
      break
    case EFFECT.GUMMY:
      effect = 'gummy'
      vars = colorVariants(main)
      break
    default:
      // SOLID, plus test/unknown effects which Discord renders as solid
      effect = 'solid'
      vars = { '--dns-main': main }
  }

  return {
    effect,
    fontClass: KNOWN_FONT_IDS.includes(styles.fontId) ? `dns-font-${styles.fontId}` : null,
    colors,
    vars
  }
}

export interface GummyLetter {
  text: string
  color?: string
  /** Letter position for the staggered squish animation */
  index?: number
}

/** Gummy colors each letter in turn; spaces and emoji keep their own color */
export function getGummyLetters(name: string, colors: string[]): GummyLetter[] {
  const graphemes = typeof Intl !== 'undefined' && 'Segmenter' in Intl
    ? Array.from(new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(name), s => s.segment)
    : Array.from(name)

  let index = 0
  return graphemes.map((text) => {
    if (text.trim().length === 0 || /\p{Extended_Pictographic}/u.test(text)) return { text }
    const letter = { text, color: colors[index % colors.length], index }
    index++
    return letter
  })
}

// ---------------------------------------------------------------------------
// Nameplates
// ---------------------------------------------------------------------------

// Discord's nameplate palettes (dark theme background color)
const NAMEPLATE_PALETTES: Record<string, string> = {
  crimson: '#900007',
  berry: '#893A99',
  sky: '#0080B7',
  teal: '#086460',
  forest: '#2D5401',
  bubble_gum: '#DC3E97',
  violet: '#730BC8',
  cobalt: '#0131C2',
  clover: '#047B20',
  lemon: '#F6CD12',
  white: '#FFFFFF',
  black: '#000000'
}

/** Tint behind the nameplate art, as Discord draws it in the member list */
export function getNameplateBackground(palette: string, hovered = false): string | null {
  const color = NAMEPLATE_PALETTES[palette]
  if (!color) return null
  return `linear-gradient(90deg, transparent 0%, ${color}14 20%, ${color}14 50%, ${color}${hovered ? '4D' : '33'} 100%)`
}

// ---------------------------------------------------------------------------
// Badges and banner
// ---------------------------------------------------------------------------

// Months of boosting needed for each Server Booster badge level (1-9)
const BOOST_LEVEL_MONTHS = [0, 2, 3, 6, 9, 12, 15, 18, 24]

export function getBoostBadgeLevel(boostingSince: string): number {
  const since = new Date(boostingSince)
  const now = new Date()
  const months = (now.getFullYear() - since.getFullYear()) * 12
    + (now.getMonth() - since.getMonth())
    - (now.getDate() < since.getDate() ? 1 : 0)
  return Math.max(1, BOOST_LEVEL_MONTHS.filter(m => months >= m).length)
}

/**
 * Most common color of an image, which Discord uses as the banner color when
 * a user has neither a banner nor a banner color. Discord's CDN allows CORS.
 */
export function getDominantColor(src: string): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      try {
        const size = 32
        const canvas = document.createElement('canvas')
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d', { willReadFrequently: true })
        if (!ctx) return resolve(null)
        ctx.drawImage(img, 0, 0, size, size)
        const { data } = ctx.getImageData(0, 0, size, size)

        // Bucket pixels by 3 bits per channel, then average the largest bucket
        const buckets = new Map<number, { count: number, r: number, g: number, b: number }>()
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3]! < 128) continue
          const r = data[i]!
          const g = data[i + 1]!
          const b = data[i + 2]!
          const key = ((r >> 5) << 6) | ((g >> 5) << 3) | (b >> 5)
          const bucket = buckets.get(key) ?? { count: 0, r: 0, g: 0, b: 0 }
          bucket.count++
          bucket.r += r
          bucket.g += g
          bucket.b += b
          buckets.set(key, bucket)
        }

        let best: { count: number, r: number, g: number, b: number } | null = null
        for (const bucket of buckets.values()) {
          if (!best || bucket.count > best.count) best = bucket
        }
        resolve(best ? rgbToHex(best.r / best.count, best.g / best.count, best.b / best.count) : null)
      } catch {
        resolve(null)
      }
    }
    img.onerror = () => resolve(null)
    img.src = src
  })
}
